<template>
  <div ref="containerRef" :style="containerStyle">
    <!-- 青绿角色（原紫色，最高最大） -->
    <div ref="purpleRef" :style="purpleBodyStyle">
      <div ref="purpleFaceRef" :style="purpleFaceStyle">
        <EyeBall size="14px" pupil-size="5px" :max-distance="4" eye-color="white" pupil-color="#2D2D2D" />
        <EyeBall size="14px" pupil-size="5px" :max-distance="4" eye-color="white" pupil-color="#2D2D2D" />
      </div>
    </div>

    <!-- 深青角色（原黑色，中高） -->
    <div ref="blackRef" :style="blackBodyStyle">
      <div ref="blackFaceRef" :style="blackFaceStyle">
        <EyeBall size="13px" pupil-size="5px" :max-distance="3" eye-color="white" pupil-color="#2D2D2D" />
        <EyeBall size="13px" pupil-size="5px" :max-distance="3" eye-color="white" pupil-color="#2D2D2D" />
      </div>
    </div>

    <!-- 浅青角色（原橙色，矮宽圆顶） -->
    <div ref="orangeRef" :style="orangeBodyStyle">
      <div ref="orangeFaceRef" :style="orangeFaceStyle">
        <Pupil size="9px" :max-distance="4" pupil-color="#2D2D2D" />
        <Pupil size="9px" :max-distance="4" pupil-color="#2D2D2D" />
      </div>
    </div>

    <!-- 金色角色（原黄色，中矮圆顶+嘴巴） -->
    <div ref="yellowRef" :style="yellowBodyStyle">
      <div ref="yellowFaceRef" :style="yellowFaceStyle">
        <Pupil size="9px" :max-distance="4" pupil-color="#2D2D2D" />
        <Pupil size="9px" :max-distance="4" pupil-color="#2D2D2D" />
      </div>
      <div ref="yellowMouthRef" :style="yellowMouthStyle" />
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, watch, toRef } from 'vue';
import gsap from 'gsap';
import Pupil from './Pupil.vue';
import EyeBall from './EyeBall.vue';

const props = defineProps({
  isTyping: { type: Boolean, default: false },
  showPassword: { type: Boolean, default: false },
  passwordLength: { type: Number, default: 0 },
});

const containerRef = ref(null);
const mouseRef = reactive({ x: 0, y: 0 });
const rafIdRef = ref(0);

const purpleRef = ref(null);
const blackRef = ref(null);
const yellowRef = ref(null);
const orangeRef = ref(null);

const purpleFaceRef = ref(null);
const blackFaceRef = ref(null);
const yellowFaceRef = ref(null);
const orangeFaceRef = ref(null);

const yellowMouthRef = ref(null);

const purpleBlinkTimerRef = ref();
const blackBlinkTimerRef = ref();
const purplePeekTimerRef = ref();

const isHidingPassword = toRef(() => props.passwordLength > 0 && !props.showPassword);
const isShowingPassword = toRef(() => props.passwordLength > 0 && props.showPassword);

const isLookingRef = ref(false);
const lookingTimerRef = ref();

const stateRef = reactive({
  isTyping: false,
  isHidingPassword: false,
  isShowingPassword: false,
  isLooking: false,
});

watch(
  () => [props.isTyping, isHidingPassword.value, isShowingPassword.value, isLookingRef.value],
  ([isTyping, isHiding, isShowing, isLooking]) => {
    stateRef.isTyping = isTyping;
    stateRef.isHidingPassword = isHiding;
    stateRef.isShowingPassword = isShowing;
    stateRef.isLooking = isLooking;
  }
);

const quickToRef = ref(null);

const containerStyle = {
  position: 'relative',
  width: '440px',
  height: '320px',
};

// 青鸾配色：青绿 #0f7c82 / 深青 #2a6d72 / 浅青 #40a0a5 / 金 #e6a23c
// 80% 尺寸
const purpleBodyStyle = ref({
  position: 'absolute',
  bottom: 0,
  left: '56px',
  width: '144px',
  height: '320px',
  backgroundColor: '#0f7c82',
  borderRadius: '8px 8px 0 0',
  zIndex: 1,
  transformOrigin: 'bottom center',
  willChange: 'transform',
});

const blackBodyStyle = ref({
  position: 'absolute',
  bottom: 0,
  left: '192px',
  width: '96px',
  height: '248px',
  backgroundColor: '#2a6d72',
  borderRadius: '6px 6px 0 0',
  zIndex: 2,
  transformOrigin: 'bottom center',
  willChange: 'transform',
});

const orangeBodyStyle = ref({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '192px',
  height: '160px',
  backgroundColor: '#40a0a5',
  borderRadius: '96px 96px 0 0',
  zIndex: 3,
  transformOrigin: 'bottom center',
  willChange: 'transform',
});

const yellowBodyStyle = ref({
  position: 'absolute',
  bottom: 0,
  left: '248px',
  width: '112px',
  height: '184px',
  backgroundColor: '#e6a23c',
  borderRadius: '56px 56px 0 0',
  zIndex: 4,
  transformOrigin: 'bottom center',
  willChange: 'transform',
});

const purpleFaceStyle = ref({
  position: 'absolute',
  display: 'flex',
  gap: '25px',
  left: '36px',
  top: '32px',
});

const blackFaceStyle = ref({
  position: 'absolute',
  display: 'flex',
  gap: '19px',
  left: '21px',
  top: '25px',
});

const orangeFaceStyle = ref({
  position: 'absolute',
  display: 'flex',
  gap: '25px',
  left: '66px',
  top: '72px',
});

const yellowFaceStyle = ref({
  position: 'absolute',
  display: 'flex',
  gap: '19px',
  left: '42px',
  top: '32px',
});

const yellowMouthStyle = ref({
  position: 'absolute',
  width: '64px',
  height: '3px',
  backgroundColor: '#2D2D2D',
  borderRadius: '9999px',
  left: '32px',
  top: '70px',
});

// Initialize GSAP
onMounted(() => {
  gsap.set('.pupil', { x: 0, y: 0 });
  gsap.set('.eyeball-pupil', { x: 0, y: 0 });
});

onMounted(() => {
  if (
    !purpleRef.value || !blackRef.value || !orangeRef.value || !yellowRef.value ||
    !purpleFaceRef.value || !blackFaceRef.value || !orangeFaceRef.value || !yellowFaceRef.value ||
    !yellowMouthRef.value
  ) return;

  const qt = {
    purpleSkew: gsap.quickTo(purpleRef.value, 'skewX', { duration: 0.3, ease: 'power2.out' }),
    blackSkew: gsap.quickTo(blackRef.value, 'skewX', { duration: 0.3, ease: 'power2.out' }),
    orangeSkew: gsap.quickTo(orangeRef.value, 'skewX', { duration: 0.3, ease: 'power2.out' }),
    yellowSkew: gsap.quickTo(yellowRef.value, 'skewX', { duration: 0.3, ease: 'power2.out' }),
    purpleX: gsap.quickTo(purpleRef.value, 'x', { duration: 0.3, ease: 'power2.out' }),
    blackX: gsap.quickTo(blackRef.value, 'x', { duration: 0.3, ease: 'power2.out' }),
    purpleHeight: gsap.quickTo(purpleRef.value, 'height', { duration: 0.3, ease: 'power2.out' }),
    purpleFaceLeft: gsap.quickTo(purpleFaceRef.value, 'left', { duration: 0.3, ease: 'power2.out' }),
    purpleFaceTop: gsap.quickTo(purpleFaceRef.value, 'top', { duration: 0.3, ease: 'power2.out' }),
    blackFaceLeft: gsap.quickTo(blackFaceRef.value, 'left', { duration: 0.3, ease: 'power2.out' }),
    blackFaceTop: gsap.quickTo(blackFaceRef.value, 'top', { duration: 0.3, ease: 'power2.out' }),
    orangeFaceX: gsap.quickTo(orangeFaceRef.value, 'x', { duration: 0.2, ease: 'power2.out' }),
    orangeFaceY: gsap.quickTo(orangeFaceRef.value, 'y', { duration: 0.2, ease: 'power2.out' }),
    yellowFaceX: gsap.quickTo(yellowFaceRef.value, 'x', { duration: 0.2, ease: 'power2.out' }),
    yellowFaceY: gsap.quickTo(yellowFaceRef.value, 'y', { duration: 0.2, ease: 'power2.out' }),
    mouthX: gsap.quickTo(yellowMouthRef.value, 'x', { duration: 0.2, ease: 'power2.out' }),
    mouthY: gsap.quickTo(yellowMouthRef.value, 'y', { duration: 0.2, ease: 'power2.out' }),
  };
  quickToRef.value = qt;

  const calcPos = (el) => {
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 3;
    const dx = mouseRef.x - cx;
    const dy = mouseRef.y - cy;
    return {
      faceX: Math.max(-15, Math.min(15, dx / 20)),
      faceY: Math.max(-10, Math.min(10, dy / 30)),
      bodySkew: Math.max(-6, Math.min(6, -dx / 120)),
    };
  };

  const calcEyePos = (el, maxDist) => {
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = mouseRef.x - cx;
    const dy = mouseRef.y - cy;
    const dist = Math.min(Math.sqrt(dx ** 2 + dy ** 2), maxDist);
    const angle = Math.atan2(dy, dx);
    return { x: Math.cos(angle) * dist, y: Math.sin(angle) * dist };
  };

  const tick = () => {
    const container = containerRef.value;
    if (!container) return;

    const { isTyping: typing, isHidingPassword: hiding, isShowingPassword: showing, isLooking: looking } = stateRef;

    if (purpleRef.value && !showing) {
      const pp = calcPos(purpleRef.value);
      if (typing || hiding) {
        qt.purpleSkew(pp.bodySkew - 12);
        qt.purpleX(32);
        qt.purpleHeight(336);
      } else {
        qt.purpleSkew(pp.bodySkew);
        qt.purpleX(0);
        qt.purpleHeight(320);
      }
    }

    if (blackRef.value && !showing) {
      const bp = calcPos(blackRef.value);
      if (looking) {
        qt.blackSkew(bp.bodySkew * 1.5 + 10);
        qt.blackX(12);
      } else if (typing || hiding) {
        qt.blackSkew(bp.bodySkew * 1.5);
        qt.blackX(0);
      } else {
        qt.blackSkew(bp.bodySkew);
        qt.blackX(0);
      }
    }

    if (orangeRef.value && !showing) {
      const op = calcPos(orangeRef.value);
      qt.orangeSkew(op.bodySkew);
    }

    if (yellowRef.value && !showing) {
      const yp = calcPos(yellowRef.value);
      qt.yellowSkew(yp.bodySkew);
    }

    if (purpleRef.value && !showing && !looking) {
      const pp = calcPos(purpleRef.value);
      const purpleFaceX = pp.faceX >= 0 ? Math.min(25, pp.faceX * 1.5) : pp.faceX;
      qt.purpleFaceLeft(45 + purpleFaceX);
      qt.purpleFaceTop(40 + pp.faceY);
    }

    if (blackRef.value && !showing && !looking) {
      const bp = calcPos(blackRef.value);
      qt.blackFaceLeft(26 + bp.faceX);
      qt.blackFaceTop(32 + bp.faceY);
    }

    if (orangeRef.value && !showing) {
      const op = calcPos(orangeRef.value);
      qt.orangeFaceX(op.faceX);
      qt.orangeFaceY(op.faceY);
    }

    if (yellowRef.value && !showing) {
      const yp = calcPos(yellowRef.value);
      qt.yellowFaceX(yp.faceX);
      qt.yellowFaceY(yp.faceY);
      qt.mouthX(yp.faceX);
      qt.mouthY(yp.faceY);
    }

    if (!showing) {
      const allPupils = container.querySelectorAll('.pupil');
      allPupils.forEach((p) => {
        const el = p;
        const maxDist = Number(el.dataset.maxDistance) || 5;
        const ePos = calcEyePos(el, maxDist);
        gsap.set(el, { x: ePos.x, y: ePos.y });
      });

      if (!looking) {
        const allEyeballs = container.querySelectorAll('.eyeball');
        allEyeballs.forEach((eb) => {
          const el = eb;
          const maxDist = Number(el.dataset.maxDistance) || 10;
          const pupil = el.querySelector('.eyeball-pupil');
          if (!pupil) return;
          const ePos = calcEyePos(el, maxDist);
          gsap.set(pupil, { x: ePos.x, y: ePos.y });
        });
      }
    }

    rafIdRef.value = requestAnimationFrame(tick);
  };

  const onMove = (e) => {
    mouseRef.x = e.clientX;
    mouseRef.y = e.clientY;
  };

  window.addEventListener('mousemove', onMove, { passive: true });
  rafIdRef.value = requestAnimationFrame(tick);

  onBeforeUnmount(() => {
    window.removeEventListener('mousemove', onMove);
    cancelAnimationFrame(rafIdRef.value);
  });
});

// 青绿角色眨眼
onMounted(() => {
  const purpleEyeballs = purpleRef.value?.querySelectorAll('.eyeball');
  if (!purpleEyeballs?.length) return;

  const scheduleBlink = () => {
    purpleBlinkTimerRef.value = setTimeout(() => {
      purpleEyeballs.forEach((el) => {
        gsap.to(el, { height: 2, duration: 0.08, ease: 'power2.in' });
      });
      setTimeout(() => {
        purpleEyeballs.forEach((el) => {
          const size = Number(el.style.width.replace('px', '')) || 14;
          gsap.to(el, { height: size, duration: 0.08, ease: 'power2.out' });
        });
        scheduleBlink();
      }, 150);
    }, Math.random() * 4000 + 3000);
  };

  scheduleBlink();
  onBeforeUnmount(() => clearTimeout(purpleBlinkTimerRef.value));
});

// 深青角色眨眼
onMounted(() => {
  const blackEyeballs = blackRef.value?.querySelectorAll('.eyeball');
  if (!blackEyeballs?.length) return;

  const scheduleBlink = () => {
    blackBlinkTimerRef.value = setTimeout(() => {
      blackEyeballs.forEach((el) => {
        gsap.to(el, { height: 2, duration: 0.08, ease: 'power2.in' });
      });
      setTimeout(() => {
        blackEyeballs.forEach((el) => {
          const size = Number(el.style.width.replace('px', '')) || 13;
          gsap.to(el, { height: size, duration: 0.08, ease: 'power2.out' });
        });
        scheduleBlink();
      }, 150);
    }, Math.random() * 4000 + 3000);
  };

  scheduleBlink();
  onBeforeUnmount(() => clearTimeout(blackBlinkTimerRef.value));
});

const applyLookAtEachOther = () => {
  const qt = quickToRef.value;
  if (qt) {
    qt.purpleFaceLeft(44);
    qt.purpleFaceTop(52);
    qt.blackFaceLeft(25);
    qt.blackFaceTop(9);
  }
  purpleRef.value?.querySelectorAll('.eyeball-pupil').forEach((p) => {
    gsap.to(p, { x: 3, y: 3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
  });
  blackRef.value?.querySelectorAll('.eyeball-pupil').forEach((p) => {
    gsap.to(p, { x: 0, y: -3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
  });
};

const applyHidingPassword = () => {
  const qt = quickToRef.value;
  if (qt) {
    qt.purpleFaceLeft(44);
    qt.purpleFaceTop(52);
  }
};

const applyShowPassword = () => {
  const qt = quickToRef.value;
  if (qt) {
    qt.purpleSkew(0);
    qt.blackSkew(0);
    qt.orangeSkew(0);
    qt.yellowSkew(0);
    qt.purpleX(0);
    qt.blackX(0);
    qt.purpleHeight(320);

    qt.purpleFaceLeft(16);
    qt.purpleFaceTop(28);
    qt.blackFaceLeft(8);
    qt.blackFaceTop(22);
    qt.orangeFaceX(40 - 66);
    qt.orangeFaceY(68 - 72);
    qt.yellowFaceX(16 - 42);
    qt.yellowFaceY(28 - 32);
    qt.mouthX(8 - 32);
    qt.mouthY(0);
  }

  purpleRef.value?.querySelectorAll('.eyeball-pupil').forEach((p) => {
    gsap.to(p, { x: -3, y: -3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
  });
  blackRef.value?.querySelectorAll('.eyeball-pupil').forEach((p) => {
    gsap.to(p, { x: -3, y: -3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
  });
  orangeRef.value?.querySelectorAll('.pupil').forEach((p) => {
    gsap.to(p, { x: -4, y: -3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
  });
  yellowRef.value?.querySelectorAll('.pupil').forEach((p) => {
    gsap.to(p, { x: -4, y: -3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
  });
};

// 密码偷瞄效果
watch(
  () => [isShowingPassword.value, props.passwordLength],
  ([showing, len]) => {
    if (!showing || len <= 0) {
      clearTimeout(purplePeekTimerRef.value);
      return;
    }

    const purpleEyePupils = purpleRef.value?.querySelectorAll('.eyeball-pupil');
    if (!purpleEyePupils?.length) return;

    const schedulePeek = () => {
      purplePeekTimerRef.value = setTimeout(() => {
        purpleEyePupils.forEach((p) => {
          gsap.to(p, { x: 3, y: 4, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
        });
        const qt = quickToRef.value;
        if (qt) {
          qt.purpleFaceLeft(16);
          qt.purpleFaceTop(28);
        }

        setTimeout(() => {
          purpleEyePupils.forEach((p) => {
            gsap.to(p, { x: -3, y: -3, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
          });
          schedulePeek();
        }, 800);
      }, Math.random() * 3000 + 2000);
    };

    schedulePeek();
    onBeforeUnmount(() => clearTimeout(purplePeekTimerRef.value));
  }
);

// 输入密码时角色看向彼此
watch(
  () => [props.isTyping, isShowingPassword.value],
  ([typing, showing]) => {
    if (typing && !showing) {
      isLookingRef.value = true;
      stateRef.isLooking = true;
      applyLookAtEachOther();

      clearTimeout(lookingTimerRef.value);
      lookingTimerRef.value = setTimeout(() => {
        isLookingRef.value = false;
        stateRef.isLooking = false;
        purpleRef.value?.querySelectorAll('.eyeball-pupil').forEach((p) => {
          gsap.killTweensOf(p);
        });
      }, 800);
    } else {
      clearTimeout(lookingTimerRef.value);
      isLookingRef.value = false;
      stateRef.isLooking = false;
    }
  }
);

// 密码显示/隐藏状态
watch(
  () => [isShowingPassword.value, isHidingPassword.value],
  ([showing, hiding]) => {
    if (showing) {
      applyShowPassword();
    } else if (hiding) {
      applyHidingPassword();
    }
  }
);
</script>